import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  integrationEmployeesFilterSource,
  IntegrationEmployeesFilter,
  toApiIntegrationEmployeesFilter,
  useIntegrationEmployeesFilters,
} from "@/pages/settings/integrations/components/integration-employees.filters.ts";
import { useIntegrationEmployees } from "@/queries/integrations/use-integrations.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiIntegrationEmployeeByType,
  ApiIntegrationEmployeeLinkStatus,
  ApiIntegrationEmployeesType,
} from "@/types/integrations/integration.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ListItem,
  Page,
  PaginationBlock,
  Stack,
  Table,
  Tag,
  TextTruncated,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

interface Props<TType extends ApiIntegrationEmployeesType> {
  integrationId: number;
  integrationType: TType;
  accountColumns: ColumnDef<ApiIntegrationEmployeeByType[TType]>[];
}

export function SettingsIntegrationEmployees<TType extends ApiIntegrationEmployeesType>({
  integrationId,
  integrationType,
  accountColumns,
}: Props<TType>) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { canIEdit } = usePermissions();
  const canViewProfile = canIEdit(ApiPermission.see_employee_directory);
  const [, setSearchParams] = useSearchParams();

  const {
    filter: { filter, typedFilters },
  } = useAppFilter<IntegrationEmployeesFilter>({
    filters: useIntegrationEmployeesFilters(),
    source: integrationEmployeesFilterSource,
  });
  const [searchTerm, setSearchTerm] = useState<string | undefined>(typedFilters.search);

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (newData) => {
      setSearchParams((searchParams) => {
        if (newData) searchParams.set("search", newData);
        else searchParams.delete("search");
        searchParams.set("page", "1");
        return searchParams;
      });
    },
  });

  const requestFilter = useMemo(() => toApiIntegrationEmployeesFilter(filter), [filter]);
  const { data, isPending } = useIntegrationEmployees(
    integrationId,
    integrationType,
    requestFilter
  );

  const columns = useMemo<ColumnDef<ApiIntegrationEmployeeByType[TType]>[]>(() => {
    const columnHelper = createColumnHelper<ApiIntegrationEmployeeByType[TType]>();
    const statusTags: Record<
      ApiIntegrationEmployeeLinkStatus,
      { variant: "positive" | "warning"; label: string }
    > = {
      [ApiIntegrationEmployeeLinkStatus.linked]: {
        variant: "positive",
        label: t("integrations:employee_link_status_linked"),
      },
      [ApiIntegrationEmployeeLinkStatus.not_found]: {
        variant: "warning",
        label: t("integrations:employee_link_status_not_found"),
      },
    };

    return [
      columnHelper.display({
        id: "employee",
        header: t("core:full_name"),
        cell: (cell) => (
          <ListItem
            title={renderEmployeeName(cell.row.original.employee)}
            avatarUrl={getEmployeeAvatarUrl(cell.row.original.employee, "sm")}
            forceShowAvatar={true}
            behavior={"card"}
            to={canViewProfile ? `/employees/${cell.row.original.employee.id}` : undefined}
            blank={true}
          />
        ),
        ...ColumnSizes.employee,
      }),
      columnHelper.display({
        id: "email",
        header: t("core:work_email"),
        cell: (cell) => <TextTruncated title={cell.row.original.email ?? "-"} />,
        ...ColumnSizes.middle,
      }),
      columnHelper.display({
        id: "status",
        header: t("common:status"),
        cell: (cell) => {
          const tag = statusTags[cell.row.original.status];
          return (
            <Stack items={"start"}>
              <Tag variant={tag.variant}>{tag.label}</Tag>
            </Stack>
          );
        },
        ...ColumnSizes.status,
      }),
      ...accountColumns,
    ];
  }, [accountColumns, canViewProfile, renderEmployeeName, t]);

  return (
    <Page.StickyLayout
      toolbar={
        <Search
          onInput={(e) => {
            setSearchTerm(e.currentTarget.value);
          }}
          type={"text"}
          value={searchTerm}
          placeholder={t("common:search")}
          clearable={true}
          onClear={() => {
            setSearchTerm(undefined);
          }}
        />
      }
      footer={
        data ? (
          <PaginationBlock
            metadata={data.metadata}
            onPageChange={(page) => {
              setSearchParams((searchParams) => {
                searchParams.set("page", page.toString());
                return searchParams;
              });
            }}
          />
        ) : undefined
      }
    >
      <Table
        data={data?.items || []}
        columns={columns}
        showSkeleton={isPending}
        stickyHeader={true}
        stickyFirstColumn={true}
        emptyStateComponent={<EmptyStateNothingFound />}
      />
    </Page.StickyLayout>
  );
}
